import { Box, Card, Block } from '@deckee/deck-hand';
import React, { FC } from 'react';
import styled, { useTheme } from 'styled-components';
import { ReactSvg, close } from '../../../../shared/vector-images';
import PlanProgressTimelineStep from './PlanProgressTimelineStep';
import { captureEvent } from '../../../../../services/event-capture';

const TextBox = styled(Box).attrs({})`
  margin-bottom: 1;
  word-break: ${(props) => props.breakWords};
  text-overflow: ${({ useEllipsis }) => (useEllipsis ? 'ellipsis' : 'initial')};
  white-space: ${({ useEllipsis }) => (useEllipsis ? 'nowrap' : 'initial')};
  overflow: ${({ useEllipsis }) => (useEllipsis ? 'hidden' : 'initial')};
`;

const StyledDescription = styled(Box)`
  text-overflow: ${({ useEllipsis }) => (useEllipsis ? 'ellipsis' : 'initial')};
  white-space: ${({ useEllipsis }) => (useEllipsis ? 'nowrap' : 'initial')};
  overflow: ${({ useEllipsis }) => (useEllipsis ? 'hidden' : 'initial')};
`;

export interface PlanProgressPointProps {
  id: string;
  heading?: React.ReactNode | string;
  useEllipsis?: boolean;
  description?: React.ReactNode;
  descriptionColor?: string;
  stepNumber?: string;
  stepType?: 'start' | 'end' | 'middle' | 'none';
  breakWordsInHeading?: 'normal' | 'all' | 'words';
  onCardClick?: (pointId: string) => void;
  onDeleteButtonClick?: (pointId: string) => void;
  isOnDeleteButtonVisible?: boolean;
}

const PlanProgressPoint: FC<PlanProgressPointProps> = ({
  id,
  heading = '',
  description = '',
  descriptionColor = 'darkergrey',
  breakWordsInHeading: breakWords,
  stepNumber = '',
  stepType = 'none',
  children,
  useEllipsis,
  onCardClick,
  onDeleteButtonClick,
  isOnDeleteButtonVisible = false,
  ...rest
}) => {
  const theme = useTheme();

  const handleOnCardClick = () => {
    captureEvent({
      type: 'plan: click on point card',
      payload: {},
    });
    if (onCardClick) {
      onCardClick(id);
    }
  };

  const handleOnDeleteButtonClick = () => {
    captureEvent({
      type: 'plan: click on delete point',
      payload: {},
    });
    if (onDeleteButtonClick) {
      onDeleteButtonClick(id);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      textDecoration="none"
      {...rest}
    >
      <PlanProgressTimelineStep stepType={stepType} stepNumber={stepNumber} />

      <Box flex="2 2 auto" overflow="hidden" alignItems="center" pt="2">
        <Card
          onClick={() => handleOnCardClick()}
          border="1px solid"
          borderColor="mediumgrey"
        >
          <Card.Body>
            <TextBox
              useEllipsis={useEllipsis}
              textDecoration="none"
              breakWords={breakWords}
              fontWeight="bold"
              fontSize="1"
            >
              {heading}
            </TextBox>
            {description && (
              <Box>
                <StyledDescription
                  useEllipsis={useEllipsis}
                  color={descriptionColor}
                  mt={heading && 1}
                >
                  {description}
                </StyledDescription>
              </Box>
            )}
            {children && <Box mt={heading && 1}>{children}</Box>}
          </Card.Body>
        </Card>
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minWidth="60px"
        pt="2"
      >
        {isOnDeleteButtonVisible && (
          <Block
            onClick={() => handleOnDeleteButtonClick()}
            height="30px"
            width="30px"
          >
            <ReactSvg
              toDisplay={close()}
              options={{
                fillColor: theme.colors.primary,
              }}
              width={30}
              height={30}
            />
          </Block>
        )}
      </Box>
    </Box>
  );
};

PlanProgressPoint.defaultProps = {
  heading: undefined,
  description: undefined,
  breakWordsInHeading: 'normal',
  descriptionColor: 'darkergrey',
  stepNumber: '',
  stepType: 'none',
  onCardClick: undefined,
  onDeleteButtonClick: undefined,
  useEllipsis: false,
  isOnDeleteButtonVisible: false,
};

export default PlanProgressPoint;
